<template>
  <div class="ai-teaching-container">
    <!-- 主内容区域 -->
    <div class="main-content">
      <!-- 顶部按钮组 -->
      <div class="button-group">
        <button v-for="button in mainButtons" :key="button.id" class="main-button"
          :class="{ active: activeMainButton === button.id }" @click="handleMainButtonClick(button.id)">
          {{ button.label }}
        </button>
      </div>

      <!-- 内容区域 -->
      <div class="content-area">
        <!-- 数据表格区域 -->
        <div class="table-content">
          <!-- 搜索和筛选栏 -->
          <div class="filter-bar">
            <div class="search-filters">
              <el-input v-model="searchParams.keyword" placeholder="搜索标题或内容" class="search-input" clearable>
                <template #prefix>
                  <el-icon>
                    <Search />
                  </el-icon>
                </template>
              </el-input>

              <el-select v-model="searchParams.training_type" placeholder="训练类型" class="type-select" clearable>
                <el-option label="全部类型" value="" />
                <el-option label="自媒体训练" value="self_media" />
                <el-option label="第三方媒体训练" value="third_media" />
                <el-option label="B2B训练" value="b2b" />
                <el-option label="智能体官网训练" value="ai_agent" />
              </el-select>

              <el-select v-model="searchParams.status" placeholder="发布状态" class="status-select" clearable>
                <el-option label="全部状态" value="" />
                <el-option label="处理中" :value="0" />
                <el-option label="发布中" :value="1" />
                <el-option label="发布成功" :value="2" />
                <el-option label="发布失败" :value="3" />
                <el-option label="部分成功" :value="4" />
              </el-select>

              <el-date-picker v-model="searchParams.dateRange" type="daterange" range-separator="至"
                start-placeholder="开始日期" end-placeholder="结束日期" class="date-picker" value-format="YYYY-MM-DD" />

              <el-button type="primary" @click="handleSearch">
                <el-icon>
                  <Search />
                </el-icon>
                搜索
              </el-button>

              <el-button @click="handleReset">
                <el-icon>
                  <Refresh />
                </el-icon>
                重置
              </el-button>

              <!-- 新增：批量操作按钮组 -->

              <el-button-group>
                <el-button type="warning" @click="handleBatchRetry"
                  :disabled="selectedItems.length === 0 || !hasRetryableItems">
                  <el-icon>
                    <RefreshRight />
                  </el-icon>
                  批量重试 ({{ retryableCount }})
                </el-button>
                <el-button type="danger" @click="handleBatchDelete" :disabled="selectedItems.length === 0">
                  <el-icon>
                    <Delete />
                  </el-icon>
                  批量删除 ({{ selectedItems.length }})
                </el-button>
              </el-button-group>
            </div>
          </div>

          <!-- 数据表格 -->
          <el-table :data="tableData" v-loading="loading" class="data-table" @selection-change="handleSelectionChange"
            :header-cell-style="{
              background: 'transparent',
              color: '#e0e6ff',
              borderBottom: '1px solid rgba(255, 112, 72, 0.3)'
            }">
            <el-table-column type="selection" width="55" />
            <el-table-column type="index" label="序号" width="60" />
            <el-table-column prop="article_title" label="文章标题" min-width="200">
              <template #default="{ row }">
                <div class="title-content" @click="handleViewArticle(row)">
                  {{ row.article_title || '无标题' }}
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="training_type" label="训练类型" width="120">
              <template #default="{ row }">
                <el-tag effect="dark" class="type-tag">
                  {{ getTrainingTypeText(row.training_type) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="user_info" label="用户" width="120">
              <template #default="{ row }">
                <div class="user-info">
                  {{ row.user_nickname || '管理员' }}
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="status" label="发布状态" width="100">
              <template #default="{ row }">
                <el-tag :type="getQueueStatusType(row.status)" effect="dark">
                  {{ getQueueStatusText(row.status) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="发布进度" width="150">
              <template #default="{ row }">
                <div class="progress-info">
                  <div class="progress-text">
                    {{ row.success_count || 0 }}/{{ row.total_platforms || 0 }}
                  </div>
                  <el-progress :percentage="getProgressPercentage(row)" :status="getProgressStatus(row)"
                    :stroke-width="6" :show-text="false" />
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="retry_count" label="重试次数" width="90">
              <template #default="{ row }">
                <span :class="{ 'error-text': row.retry_count > 3 }">
                  {{ row.retry_count }}次
                </span>
              </template>
            </el-table-column>
            <el-table-column prop="create_time" label="创建时间" width="160" />
            <el-table-column prop="update_time" label="更新时间" width="160" />
            <el-table-column label="操作" fixed="right" width="300">
              <template #default="{ row }">
                <el-button size="small" @click="handleViewArticle(row)" class="action-btn">
                  查看文章
                </el-button>
                <el-button size="small" type="info" @click="handleViewRecords(row)" class="action-btn">
                  发布记录
                </el-button>
                <el-button size="small" type="primary" @click="handleRetryQueue(row)"
                  :disabled="row.status !== 3 && row.status !== 4" class="action-btn">
                  重试队列
                </el-button>
                <el-button size="small" type="danger" @click="handleDeleteQueue(row)" class="action-btn">
                  删除
                </el-button>
              </template>
            </el-table-column>
          </el-table>

          <!-- 分页 -->
          <div class="pagination">
            <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.pageSize"
              :page-sizes="[10, 20, 50, 100]" :total="pagination.total" layout="total, sizes, prev, pager, next, jumper"
              @size-change="handleSizeChange" @current-change="handleCurrentChange" />
          </div>
        </div>
      </div>
    </div>

    <!-- 文章详情对话框 -->
    <el-dialog v-model="showArticleDialog" :title="articleDetail.title" width="800px" :close-on-click-modal="false"
      class="article-dialog">
      <div class="article-detail">
        <div class="article-meta">
          <div class="meta-item">
            <span class="meta-label">训练类型：</span>
            <el-tag size="small">{{ getTrainingTypeText(articleDetail.training_type) }}</el-tag>
          </div>
          <div class="meta-item">
            <span class="meta-label">发布状态：</span>
            <el-tag :type="getQueueStatusType(articleDetail.status)" size="small">
              {{ getQueueStatusText(articleDetail.status) }}
            </el-tag>
          </div>
          <div class="meta-item">
            <span class="meta-label">创建时间：</span>
            <span>{{ articleDetail.create_time }}</span>
          </div>
          <div class="meta-item">
            <span class="meta-label">更新时间：</span>
            <span>{{ articleDetail.update_time }}</span>
          </div>
        </div>

        <div class="article-content">
          <h3 class="content-title">文章内容</h3>
          <div class="content-body" v-html="formatContent(articleDetail.content)"></div>
        </div>
      </div>

      <template #footer>
        <el-button @click="showArticleDialog = false">关闭</el-button>
      </template>
    </el-dialog>

    <!-- 发布记录对话框 -->
    <el-dialog v-model="showRecordsDialog" :title="`发布记录 - ${currentQueue.article_title}`" width="1200px"
      :close-on-click-modal="false" class="records-dialog">
      <div class="records-content">
        <!-- 发布概览 -->
        <div class="records-overview">
          <div class="overview-cards">
            <div class="overview-card">
              <div class="overview-value">{{ currentQueue.total_platforms || 0 }}</div>
              <div class="overview-label">总平台数</div>
            </div>
            <div class="overview-card">
              <div class="overview-value success">{{ currentQueue.success_count || 0 }}</div>
              <div class="overview-label">成功</div>
            </div>
            <div class="overview-card">
              <div class="overview-value pending">{{ getPendingCount(currentQueue) }}</div>
              <div class="overview-label">进行中</div>
            </div>
            <div class="overview-card">
              <div class="overview-value failed">{{ currentQueue.failed_count || 0 }}</div>
              <div class="overview-label">失败</div>
            </div>
          </div>
        </div>

        <!-- 发布记录表格 -->
        <el-table :data="recordsData" v-loading="recordsLoading" class="records-table">
          <el-table-column prop="record_id" label="序号" width="60" />
          <el-table-column prop="platform_text" label="发布平台" width="120" />
          <el-table-column prop="field_type_text" label="账号类型" width="120" />
          <el-table-column prop="ghz_name" label="账号名称" width="150" />
          <el-table-column prop="status" label="发布状态" width="100">
            <template #default="{ row }">
              <el-tag :type="getPublishRecordStatusType(row.status)" size="small">
                {{ row.status_text }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="retry_count" label="重试次数" width="90">
            <template #default="{ row }">
              <span :class="{ 'error-text': row.retry_count > 3 }">
                {{ row.retry_count }}次
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="publish_url" label="发布链接" min-width="150">
            <template #default="{ row }">
              <div v-if="row.publish_url" class="publish-link">
                <a :href="row.publish_url" target="_blank" class="link-text">查看链接</a>
              </div>
              <span v-else>-</span>
            </template>
          </el-table-column>
          <el-table-column prop="error_message" label="错误信息" min-width="200">
            <template #default="{ row }">
              <div class="error-message-cell" v-if="row.error_message">
                <el-tooltip effect="dark" :content="row.error_message" placement="top-start">
                  <div class="error-text-truncate">
                    {{ truncateErrorMessage(row.error_message) }}
                  </div>
                </el-tooltip>
              </div>
              <span v-else>-</span>
            </template>
          </el-table-column>
          <el-table-column prop="publish_time" label="发布时间" width="160" />
          <el-table-column label="操作" fixed="right" width="120">
            <template #default="{ row }">
              <el-button size="small" type="primary" @click="handleRetryRecord(row)" :disabled="row.status !== 3">
                重试
              </el-button>
            </template>
          </el-table-column>
        </el-table>

        <!-- 分页 -->
        <div class="pagination">
          <el-pagination v-model:current-page="recordsPagination.page" v-model:page-size="recordsPagination.pageSize"
            :page-sizes="[10, 20, 50]" :total="recordsPagination.total" layout="total, sizes, prev, pager, next, jumper"
            @size-change="handleRecordsSizeChange" @current-change="handleRecordsCurrentChange" />
        </div>
      </div>

      <template #footer>
        <el-button @click="showRecordsDialog = false">关闭</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ElMessage, ElMessageBox } from 'element-plus'
import { Search, Refresh, RefreshRight, Delete } from '@element-plus/icons-vue'
import axios from 'axios'
import config from '@/config.js'
import Cookies from 'js-cookie'

export default {
  name: 'AiTeaching',
  components: {
    Search,
    Refresh,
    RefreshRight,
    Delete
  },
  data() {
    return {
      // 响应式数据
      activeMainButton: 'all',
      loading: false,
      showArticleDialog: false,
      showRecordsDialog: false,
      selectedItems: [],

      // 主按钮配置
      mainButtons: [
        { id: 'all', label: '全部发布' },
        { id: 'pending', label: '处理中' },
        { id: 'processing', label: '发布中' },
        { id: 'success', label: '发布成功' },
        { id: 'failed', label: '发布失败' },
        { id: 'partial', label: '部分成功' }
      ],

      // 搜索参数
      searchParams: {
        keyword: '',
        training_type: '',
        status: '',
        dateRange: []
      },

      // 分页数据
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      },

      // 表格数据
      tableData: [],

      // 文章详情
      articleDetail: {
        title: '',
        content: '',
        training_type: '',
        status: 0,
        create_time: '',
        update_time: ''
      },

      // 当前选中的队列
      currentQueue: {},

      // 发布记录数据
      recordsData: [],
      recordsLoading: false,
      recordsPagination: {
        page: 1,
        pageSize: 10,
        total: 0
      }
    }
  },
  // 添加计算属性
  computed: {
    userInfo() {
      return JSON.parse(Cookies.get('user_info') || '{}')
    },
    // 计算可以重试的项目数量
    retryableCount() {
      return this.selectedItems.filter(item => item.status === 3 || item.status === 4).length
    },
    // 检查是否有可以重试的项目
    hasRetryableItems() {
      return this.retryableCount > 0
    }
  },
  methods: {
    handleMainButtonClick(buttonId) {
      this.activeMainButton = buttonId

      // 根据按钮设置筛选条件
      switch (buttonId) {
        case 'pending':
          this.searchParams.status = 0
          break
        case 'processing':
          this.searchParams.status = 1
          break
        case 'success':
          this.searchParams.status = 2
          break
        case 'failed':
          this.searchParams.status = 3
          break
        case 'partial':
          this.searchParams.status = 4
          break
        default:
          this.searchParams.status = ''
      }

      this.pagination.page = 1
      this.loadTableData()
    },

    handleSearch() {
      this.pagination.page = 1
      this.loadTableData()
    },

    handleReset() {
      this.searchParams = {
        keyword: '',
        training_type: '',
        status: '',
        dateRange: []
      }
      this.activeMainButton = 'all'
      this.pagination.page = 1
      this.loadTableData()
    },

    // 获取训练类型文本
    getTrainingTypeText(type) {
      const typeMap = {
        'self_media': '自媒体训练',
        'third_media': '第三方媒体训练',
        'b2b': 'B2B训练',
        'ai_agent': '智能体官网训练'
      }
      return typeMap[type] || type
    },

    // 获取队列状态文本
    getQueueStatusText(status) {
      const statusMap = {
        0: '处理中',
        1: '发布中',
        2: '发布成功',
        3: '发布失败',
        4: '部分成功'
      }
      return statusMap[status] || '未知'
    },

    // 获取队列状态类型
    getQueueStatusType(status) {
      const statusMap = {
        0: 'warning',
        1: 'info',
        2: 'success',
        3: 'danger',
        4: 'warning'
      }
      return statusMap[status] || 'info'
    },

    // 修改进行中数量的计算方法
    getPendingCount(queue) {
      // 基于总平台数和成功、失败数计算进行中数量
      const total = parseInt(queue.total_platforms) || 0
      const success = parseInt(queue.success_count) || 0
      const failed = parseInt(queue.failed_count) || 0
      const pending = total - success - failed
      return Math.max(pending, 0) // 确保不为负数
    },

    // 截断错误信息
    truncateErrorMessage(errorMessage) {
      if (!errorMessage) return '';
      return errorMessage.length > 30 ? errorMessage.substring(0, 30) + '...' : errorMessage;
    },

    // 获取发布记录状态类型
    getPublishRecordStatusType(status) {
      const statusMap = {
        0: 'warning',
        1: 'info',
        2: 'success',
        3: 'danger'
      };
      return statusMap[status] || 'info';
    },

    // 修改进度百分比计算
    getProgressPercentage(row) {
      const total = parseInt(row.total_platforms) || 0
      const success = parseInt(row.success_count) || 0
      if (total === 0) return 0
      const percentage = Math.round((success / total) * 100)
      return Math.min(Math.max(percentage, 0), 100) // 确保在0-100范围内
    },

    // 修改进度状态
    getProgressStatus(row) {
      const percentage = this.getProgressPercentage(row)
      if (percentage === 100) return 'success'
      if (row.status === 3) return 'exception' // 失败状态显示异常
      if (row.status === 4 && percentage > 0) return 'warning' // 部分成功显示警告
      return null
    },

    // 选择变化
    handleSelectionChange(selection) {
      this.selectedItems = selection
    },

    // 加载表格数据
    async loadTableData() {
      this.loading = true
      try {
        const params = {
          action: 'publish_queue_list',
          store_id: this.userInfo.id,
          page: this.pagination.page,
          page_size: this.pagination.pageSize,
          ...this.searchParams
        }

        // 处理日期范围
        if (this.searchParams.dateRange && this.searchParams.dateRange.length === 2) {
          params.start_date = this.searchParams.dateRange[0]
          params.end_date = this.searchParams.dateRange[1]
        }

        const response = await axios.post(
          `${config.BASE_URL}/site/api/training_management.php`,
          params
        )

        if (response.data.success) {
          this.tableData = response.data.data
          this.pagination.total = response.data.pagination.total
        } else {
          ElMessage.error(response.data.error || '加载数据失败')
        }
      } catch (error) {
        console.error('加载数据失败:', error)
        ElMessage.error('加载数据失败')
      } finally {
        this.loading = false
      }
    },

    // 加载发布记录
    async loadPublishRecords(queueId) {
      this.recordsLoading = true
      try {
        const response = await axios.post(
          `${config.BASE_URL}/site/api/training_management.php`,
          {
            action: 'get_publish_records',
            store_id: this.userInfo.id,
            queue_id: queueId,
            page: this.recordsPagination.page,
            page_size: this.recordsPagination.pageSize
          }
        )

        if (response.data.success) {
          this.recordsData = response.data.data
          this.recordsPagination.total = response.data.pagination.total
        } else {
          ElMessage.error(response.data.error || '加载发布记录失败')
        }
      } catch (error) {
        console.error('加载发布记录失败:', error)
        ElMessage.error('加载发布记录失败')
      } finally {
        this.recordsLoading = false
      }
    },

    handleSizeChange(size) {
      this.pagination.pageSize = size
      this.pagination.page = 1
      this.loadTableData()
    },

    handleCurrentChange(page) {
      this.pagination.page = page
      this.loadTableData()
    },

    handleRecordsSizeChange(size) {
      this.recordsPagination.pageSize = size
      this.recordsPagination.page = 1
      this.loadPublishRecords(this.currentQueue.id)
    },

    handleRecordsCurrentChange(page) {
      this.recordsPagination.page = page
      this.loadPublishRecords(this.currentQueue.id)
    },

    // 查看文章详情
    async handleViewArticle(row) {
      try {
        const response = await axios.post(
          `${config.BASE_URL}/site/api/training_management.php`,
          {
            action: 'get_article_detail',
            store_id: this.userInfo.id,
            article_id: row.article_id
          }
        )

        if (response.data.success) {
          this.articleDetail = {
            ...response.data.data,
            training_type: row.training_type,
            status: row.status,
            create_time: row.create_time,
            update_time: row.update_time
          }
          this.showArticleDialog = true
        } else {
          ElMessage.error('获取文章详情失败')
        }
      } catch (error) {
        console.error('获取文章详情失败:', error)
        ElMessage.error('获取文章详情失败')
      }
    },

    // 查看发布记录
    async handleViewRecords(row) {
      this.currentQueue = row
      this.recordsPagination.page = 1
      this.showRecordsDialog = true
      await this.loadPublishRecords(row.id)
    },

    // 重试单个发布记录 - 添加调试
    async handleRetryRecord(recordsData) {
      try {
        await ElMessageBox.confirm(
          `确定要重新发布到 ${recordsData.platform_text} 吗？`,
          '确认重试',
          { confirmButtonText: '确定', cancelButtonText: '取消', type: 'warning' }
        );

        const response = await axios.post(
          `${config.BASE_URL}/site/api/training_management.php`,
          {
            action: 'retry_single_record',
            store_id: this.userInfo.id,
            record_id: recordsData.record_id
          }
        );

        console.log('重试单个记录响应:', response.data);

        if (response.data.success) {
          ElMessage.success('发布记录已加入重试队列');
          this.loadPublishRecords(this.currentQueue.id);
          this.loadTableData(); // 刷新主表格
        } else {
          ElMessage.error(response.data.error || '重试失败');
        }
      } catch (error) {
        console.error('重试单个记录错误:', error);
        if (error !== 'cancel') {
          ElMessage.error('重试失败: ' + error.message);
        }
      }
    },

    // 重试整个队列
    async handleRetryQueue(row) {
      try {
        await ElMessageBox.confirm(
          `确定要重试整个队列吗？只会重试失败的发布记录。`,
          '确认重试队列',
          { confirmButtonText: '确定', cancelButtonText: '取消', type: 'warning' }
        );

        const response = await axios.post(
          `${config.BASE_URL}/site/api/training_management.php`,
          {
            action: 'retry_queue',
            store_id: this.userInfo.id,
            queue_id: row.id
          }
        );

        if (response.data.success) {
          ElMessage.success(response.data.message);
          this.loadTableData();
        } else {
          ElMessage.error(response.data.error || '队列重试失败');
        }
      } catch (error) {
        if (error !== 'cancel') {
          ElMessage.error('队列重试失败');
        }
      }
    },

    // 修改批量重试方法
    async handleBatchRetry() {
      if (this.selectedItems.length === 0) {
        ElMessage.warning('请选择要重试的任务')
        return
      }

      // 筛选出可以重试的任务（失败或部分成功）
      const retryableItems = this.selectedItems.filter(item => item.status === 3 || item.status === 4)
      if (retryableItems.length === 0) {
        ElMessage.warning('选中的任务中没有可以重试的任务（只有失败或部分成功的任务可以重试）')
        return
      }

      try {
        await ElMessageBox.confirm(
          `确定要重试 ${retryableItems.length} 个失败的任务吗？`,
          '批量重试',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )

        const queueIds = retryableItems.map(item => item.id)

        const response = await axios.post(
          `${config.BASE_URL}/site/api/training_management.php`,
          {
            action: 'batch_retry_publish',
            store_id: this.userInfo.id,
            queue_ids: queueIds
          }
        )

        if (response.data.success) {
          ElMessage.success(response.data.message)
          this.selectedItems = []
          this.loadTableData() // 刷新表格数据
        } else {
          ElMessage.error(response.data.error || '批量重试失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          ElMessage.error('批量重试失败')
        }
      }
    },

    // 删除单个队列
    async handleDeleteQueue(row) {
      try {
        await ElMessageBox.confirm(
          `确定要删除这个发布队列吗？此操作不可恢复。`,
          '确认删除',
          { confirmButtonText: '确定', cancelButtonText: '取消', type: 'warning' }
        );

        const response = await axios.post(
          `${config.BASE_URL}/site/api/training_management.php`,
          {
            action: 'delete_queue',
            store_id: this.userInfo.id,
            queue_id: row.id
          }
        );

        if (response.data.success) {
          ElMessage.success('删除成功');
          this.loadTableData();
        } else {
          ElMessage.error(response.data.error || '删除失败');
        }
      } catch (error) {
        if (error !== 'cancel') {
          ElMessage.error('删除失败');
        }
      }
    },

    // 批量删除
    async handleBatchDelete() {
      if (this.selectedItems.length === 0) {
        ElMessage.warning('请选择要删除的任务')
        return
      }

      try {
        await ElMessageBox.confirm(
          `确定要删除选中的 ${this.selectedItems.length} 个任务吗？此操作不可恢复。`,
          '批量删除',
          { confirmButtonText: '确定', cancelButtonText: '取消', type: 'warning' }
        )

        const queueIds = this.selectedItems.map(item => item.id)

        const response = await axios.post(
          `${config.BASE_URL}/site/api/training_management.php`,
          {
            action: 'batch_delete_queue',
            store_id: this.userInfo.id,
            queue_ids: queueIds
          }
        )

        if (response.data.success) {
          ElMessage.success(`已成功删除 ${this.selectedItems.length} 个任务`)
          this.selectedItems = []
          this.loadTableData()
        } else {
          ElMessage.error(response.data.error || '批量删除失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          ElMessage.error('批量删除失败')
        }
      }
    },

    // 格式化内容显示
    formatContent(content) {
      if (!content) return ''
      return content.replace(/\n/g, '<br>')
    }
  },
  mounted() {
    this.loadTableData()
  }
}
</script>

<style scoped lang="scss">
.ai-teaching-container {
  padding: 24px;
  min-height: calc(100vh - 160px);
  background: transparent;
}

.main-content {
  background: hsla(0, 0%, 100%, .1);
  border-radius: 16px;
  padding: 24px;
  backdrop-filter: blur(10px);
}

.button-group {
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}

.main-button {
  padding: 12px 24px;
  background: hsla(0, 0%, 100%, .1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: #e0e6ff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #ff7048;
    border-color: #ff7048;
    transform: translateY(-2px);
  }

  &.active {
    background: #ff7048;
    border-color: #ff7048;
  }
}

.content-area {
  min-height: 400px;
}

.table-content {
  background: hsla(0, 0%, 100%, .1);
  border-radius: 8px;
  padding: 20px;
}

.filter-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 16px;

  .search-filters {
    display: flex;
    gap: 12px;
    align-items: center;
    flex-wrap: wrap;

    .search-input {
      width: 200px;
    }

    .type-select,
    .status-select {
      width: 150px;
    }

    .date-picker {
      width: 240px;
    }
  }
}

.data-table {
  background: transparent;

  :deep(.el-table) {
    background: transparent;

    th {
      background: transparent !important;
      color: #e0e6ff !important;
      border-bottom: 1px solid rgba(255, 112, 72, 0.3) !important;
    }

    tr {
      background: rgba(255, 255, 255, 0.05);

      &:hover {
        background: rgba(255, 255, 255, 0.1) !important;
      }
    }
  }

  .title-content {
    color: #e0e6ff;
    font-weight: 500;
    line-height: 1.4;
    cursor: pointer;
    transition: color 0.3s ease;

    &:hover {
      color: #ff7048;
    }
  }

  .type-tag {
    background: rgba(255, 112, 72, 0.2);
    border: 1px solid rgba(255, 112, 72, 0.3);
    color: #ff7048;
  }

  .user-info {
    color: #e0e6ff;
  }

  .error-text {
    color: #f56c6c;
    font-weight: 500;
  }

  .action-btn {
    margin: 2px;
  }
}

.pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
  padding: 20px 0;
}

// 文章详情样式
.article-detail {
  .article-meta {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
    margin-bottom: 24px;
    padding: 16px;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 6px;

    .meta-item {
      display: flex;
      align-items: center;
      gap: 8px;

      .meta-label {
        color: #a0a8b8;
        font-size: 14px;
      }
    }
  }

  .article-content {
    margin-bottom: 24px;

    .content-title {
      color: #e0e6ff;
      font-size: 18px;
      margin-bottom: 12px;
      padding-bottom: 8px;
      border-bottom: 1px solid rgba(255, 112, 72, 0.3);
    }

    .content-body {
      color: #a0a8b8;
      line-height: 1.8;
      max-height: 400px;
      overflow-y: auto;
      padding: 16px;
      background: rgba(255, 255, 255, 0.03);
      border-radius: 6px;
      border: 1px solid rgba(255, 255, 255, 0.1);
    }
  }
}

.progress-info {
  .progress-text {
    font-size: 12px;
    color: #e0e6ff;
    margin-bottom: 4px;
  }
}

.records-content {
  .records-overview {
    margin-bottom: 20px;

    .overview-cards {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 16px;

      .overview-card {
        background: rgba(255, 255, 255, 0.05);
        border-radius: 8px;
        padding: 16px;
        text-align: center;
        border: 1px solid rgba(255, 255, 255, 0.1);

        .overview-value {
          font-size: 24px;
          font-weight: bold;
          margin-bottom: 8px;

          &.success {
            color: #67C23A;
          }

          &.pending {
            color: #E6A23C;
          }

          &.failed {
            color: #F56C6C;
          }
        }

        .overview-label {
          color: #a0a8b8;
          font-size: 12px;
        }
      }
    }
  }
}

.records-table {
  :deep(.el-table) {
    background: transparent;

    th {
      background: rgba(255, 255, 255, 0.05) !important;
      color: #e0e6ff !important;
    }

    tr {
      background: rgba(255, 255, 255, 0.03);

      &:hover {
        background: rgba(255, 255, 255, 0.05) !important;
      }
    }
  }
}

.publish-link {
  .link-text {
    color: #409EFF;
    text-decoration: none;
    font-size: 12px;

    &:hover {
      text-decoration: underline;
    }
  }
}

.error-message-cell {
  .error-text-truncate {
    color: #f56c6c;
    font-size: 12px;
    line-height: 1.4;
    cursor: help;
  }
}

// 响应式设计
@media (max-width: 768px) {
  .ai-teaching-container {
    padding: 16px;
  }

  .button-group {
    gap: 12px;

    .main-button {
      padding: 10px 20px;
      font-size: 13px;
    }
  }

  .filter-bar {
    flex-direction: column;
    align-items: stretch;

    .search-filters {
      justify-content: center;

      .search-input,
      .type-select,
      .status-select,
      .date-picker {
        width: 100%;
        max-width: 200px;
      }
    }
  }

  .overview-cards {
    grid-template-columns: repeat(2, 1fr) !important;
  }

  .article-meta {
    grid-template-columns: 1fr !important;
  }
}

@media (max-width: 480px) {
  .main-content {
    padding: 16px;
  }

  .button-group {
    justify-content: center;
  }
}
</style>

<style>
.article-dialog .el-dialog,
.records-dialog .el-dialog {
  background: rgba(13, 5, 26, 0.95) !important;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 112, 72, 0.3);
  border-radius: 16px;
}

.records-dialog .el-dialog__body {
  max-height: 70vh;
  overflow-y: auto;
}

.article-dialog .el-dialog__header,
.records-dialog .el-dialog__header {
  margin: 0 !important;
  padding: 20px !important;
  border-bottom: 1px solid rgba(255, 112, 72, 0.2) !important;
}

.article-dialog .el-dialog__title,
.records-dialog .el-dialog__title {
  color: #ffffff !important;
  font-weight: 600;
  font-size: 18px;
}

.article-dialog .el-dialog__body,
.records-dialog .el-dialog__body {
  padding: 20px !important;
  background: transparent !important;
}

.article-dialog .el-dialog__footer,
.records-dialog .el-dialog__footer {
  border-top: 1px solid rgba(255, 112, 72, 0.2) !important;
  padding: 16px 20px !important;
  margin: 0 !important;
}

.el-table {
  --el-table-bg-color: transparent !important;
  --el-table-tr-bg-color: transparent !important;
  --el-table-header-bg-color: transparent !important;
  --el-table-row-hover-bg-color: rgba(255, 255, 255, 0.05) !important;
  --el-table-header-text-color: #e0e6ff !important;
}

.el-table th,
.el-table tr,
.el-table td {
  background: transparent !important;
  color: #e0e6ff !important;
}
</style>